import React, { useEffect, useRef, useState } from "react";
import Checkbox from "components/common/InputField/Checkbox";
import Button from "components/common/Button";
import { AccountPermissions } from "types/permissions";
import { useNavigate } from "react-router-dom";
import { saveToStorage } from "helpers";
import AgentSideDrawer from "components/common/AgentSideDrawer";
import SynthTooltip from "components/common/SynthTooltip";
import ToggleInput from "components/common/InputField/ToggleInput";
import SelectInput from "components/common/InputField/SelectInput";
import TextareaInput from "components/common/InputField/TextareaInput";
import { debounce } from "lodash";
import { camelCaseToRegularCase } from "helpers/camelCaseToRegularCase";

type InventoryManagementProps = {
    currentStep: string;
    setCurrentStep: (step: string) => void;
    agentSteps: Record<string, any>;
    currentStepIndex: number;
    handleAgentStepChange: (index: number, name: string, value: any) => void;
    handleApproverChange: (value: any) => void;
    setAddTeamMemberModal: (value: boolean) => void;
    setDebouncedSearchTeamMember: (value: string) => void;
    fetchingTeamMember: boolean;
    teamMembers: Record<string, any>[];
    selectedAction: string;
    setSelectedAction: (value: string) => void;
};

const InventoryManagement = ({
    currentStep,
    setCurrentStep,
    agentSteps,
    currentStepIndex,
    handleAgentStepChange,
    handleApproverChange,
    setAddTeamMemberModal,
    setDebouncedSearchTeamMember,
    fetchingTeamMember,
    teamMembers,
    selectedAction,
    setSelectedAction,
}: InventoryManagementProps) => {
    const navigate = useNavigate();
    const modalRef = useRef<HTMLDivElement>(null);
    const [actionChanged, setActionChanged] = useState(false);

    return (
        <>
            {/* Detect Inventory Action, detect_inventory_action */}
            <AgentSideDrawer open={currentStep === "detect_inventory_action"} modalRef={modalRef}>
                <div className=" flex  !bg-white flex-col  font-rocGroteskMedium  ">
                    <div className="relative">
                        <div className="px-6 py-6 !sticky !top-0 bg-white !z-10 ">
                            <div className="flex justify-between  items-center">
                                <p className="text-sm font-rocGroteskMedium text-slate-800">
                                    AI Teammate's customization
                                </p>
                                <i
                                    className="ri-close-fill text-xl text-slate-500 cursor-pointer border border-slate-200 rounded py-0 px-1"
                                    onClick={() => {
                                        setCurrentStep("");
                                    }}
                                ></i>
                            </div>
                            <p className="text-sm text-slate-500 ">
                                Set how you want this AI team-mate to work
                            </p>
                        </div>
                        <div className="mx-6 border border-slate-200 p-4 rounded-lg">
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <div className="flex gap-0.5 items-center">
                                        <p className="text-sm text-slate-800">
                                            Detect Inventory Action
                                        </p>
                                        <SynthTooltip
                                            divId={agentSteps[currentStepIndex]?.key}
                                            position={"top"}
                                            icon={
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            }
                                            contentIcon={<></>}
                                            title=""
                                            content={agentSteps[currentStepIndex]?.description}
                                            styling={{
                                                width: "270px",
                                                background: "#142837",
                                                borderRadius: "6px",
                                            }}
                                            contentClass="text-xs"
                                        />
                                    </div>

                                    {agentSteps[currentStepIndex]?.showApproval && (
                                        <div className="flex items-center gap-1">
                                            <p className="text-gm-45 text-xs">
                                                <p className="text-gm-45 text-xs">
                                                    Turn{" "}
                                                    {agentSteps[currentStepIndex]?.approvalPossible
                                                        ? "off"
                                                        : "on"}{" "}
                                                    need for approval
                                                </p>
                                            </p>
                                            <ToggleInput
                                                checked={
                                                    agentSteps[currentStepIndex]
                                                        ?.approvalPossible as boolean
                                                }
                                                name={"approvalPossible"}
                                                onChange={(e) => {
                                                    handleAgentStepChange(
                                                        currentStepIndex,
                                                        "approvalPossible",
                                                        e.target.checked ? true : false
                                                    );
                                                }}
                                                className="w-slider before:!h-4 before:!w-4 h-5 w-[30px] before:left-[2px] rounded-[30px]"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                    <p className="text-slate-500 text-sm">
                                        {agentSteps[currentStepIndex]?.approvalPossible
                                            ? "Select approver"
                                            : "Add custom instructions"}
                                    </p>
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                </div>

                                {agentSteps[currentStepIndex]?.approvalPossible && (
                                    <SelectInput
                                        value={
                                            agentSteps[currentStepIndex]?.approverName ||
                                            (agentSteps[currentStepIndex]
                                                ?.approvalIdString as string) ||
                                            ""
                                        }
                                        name="approvalIdString"
                                        nameFirst={true}
                                        placeholder={"Select approver"}
                                        handleChange={(name, value) => {
                                            handleApproverChange(value);
                                        }}
                                        handleInputChange={debounce((evt) => {
                                            setDebouncedSearchTeamMember(evt.target.value);
                                        }, 800)}
                                        isRequired={agentSteps[currentStepIndex]?.approvalPossible}
                                        searchLoading={fetchingTeamMember}
                                        dropdownOptions={teamMembers?.map((member) => ({
                                            label: member?.fullName
                                                ? member?.fullName
                                                : member?.email,
                                            value: member?._id,
                                        }))}
                                        dropdownClassName="!pb-0 !max-h-[320px]"
                                        rightDropdownContainerClassName="!sticky bottom-0"
                                        customRightDropdownContainer={
                                            <div className="py-1 px-1  flex items-center  bg-white !h-[48px] sticky w-full bottom-0 border-t border-slate-200">
                                                <div
                                                    className="flex items-center w-fit cursor-pointer gap-2"
                                                    onClick={() => {
                                                        setAddTeamMemberModal(true);
                                                    }}
                                                >
                                                    <i className="ri-add-fill text-[#D39901] text-[10px] rounded-full py-0.5 px-1 bg-[#FFF5DA]"></i>

                                                    <span className="text-[12px] font-rocGroteskMedium text-[#D39901] underline">
                                                        Add a new team-mate
                                                    </span>
                                                </div>
                                            </div>
                                        }
                                    />
                                )}

                                <TextareaInput
                                    name={"customInstructions"}
                                    value={
                                        (agentSteps[currentStepIndex]
                                            ?.customInstructions as string) || ""
                                    }
                                    onChange={(e) => {
                                        handleAgentStepChange(
                                            currentStepIndex,
                                            "customInstructions",
                                            e.target.value
                                        );
                                    }}
                                    rows={3}
                                    placeholder={"Enter custom instructions"}
                                    inputPlaceholder="Provide any specific instructions you'd like the AI Agent to follow. "
                                    required={false}
                                />
                            </div>

                            <div className="mt-4">
                                <p className="text-sm text-slate-500 p-3 bg-slate-100 border border-slate-200 rounded">
                                    Custom instructions let you tailor how your AI Agent behaves in
                                    specific steps of a workflow. Use them to guide the Agent's
                                    tone, priorities, or handling of unique business rules. This
                                    helps ensure your AI Agent aligns with your brand's voice and
                                    operational standards.
                                </p>
                            </div>
                        </div>
                        {agentSteps[currentStepIndex]?.actions?.length > 0 && (
                            <div className="mb-10">
                                <div className="flex justify-between my-6 mx-6">
                                    <div className="flex gap-1 items-center">
                                        <p className="text-sm text-[#0046A2]">
                                            Next steps after order is created
                                        </p>
                                        <span className="text-xs text-slate-500">(Optional)</span>
                                        <SynthTooltip
                                            divId={"optional-next-steps"}
                                            position={"top"}
                                            icon={
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            }
                                            contentIcon={<></>}
                                            title=""
                                            content={
                                                agentSteps[currentStepIndex]?.actions?.[0]
                                                    ?.description
                                            }
                                            styling={{
                                                width: "220px",
                                                background: "#142837",
                                                borderRadius: "6px",
                                            }}
                                            contentClass="text-xs"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AgentSideDrawer>

            {/* Detect Inventory Action, extract_inventory_report */}
            <AgentSideDrawer open={currentStep === "extract_inventory_report"} modalRef={modalRef}>
                <div className=" flex  !bg-white flex-col  font-rocGroteskMedium  ">
                    <div className="relative">
                        <div className="px-6 py-6 !sticky !top-0 bg-white !z-10 ">
                            <div className="flex justify-between  items-center">
                                <p className="text-sm font-rocGroteskMedium text-slate-800">
                                    AI Teammate's customization
                                </p>
                                <i
                                    className="ri-close-fill text-xl text-slate-500 cursor-pointer border border-slate-200 rounded py-0 px-1"
                                    onClick={() => {
                                        setCurrentStep("");
                                    }}
                                ></i>
                            </div>
                            <p className="text-sm text-slate-500 ">
                                Set how you want this AI team-mate to work
                            </p>
                        </div>
                        <div className="mx-6 border border-slate-200 p-4 rounded-lg">
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <div className="flex gap-0.5 items-center">
                                        <p className="text-sm text-slate-800">
                                            Extract Inventory Report{" "}
                                        </p>
                                        <SynthTooltip
                                            divId={agentSteps[currentStepIndex]?.key}
                                            position={"top"}
                                            icon={
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            }
                                            contentIcon={<></>}
                                            title=""
                                            content={agentSteps[currentStepIndex]?.description}
                                            styling={{
                                                width: "270px",
                                                background: "#142837",
                                                borderRadius: "6px",
                                            }}
                                            contentClass="text-xs"
                                        />
                                    </div>

                                    {agentSteps[currentStepIndex]?.showApproval && (
                                        <div className="flex items-center gap-1">
                                            <p className="text-gm-45 text-xs">
                                                <p className="text-gm-45 text-xs">
                                                    Turn{" "}
                                                    {agentSteps[currentStepIndex]?.approvalPossible
                                                        ? "off"
                                                        : "on"}{" "}
                                                    need for approval
                                                </p>
                                            </p>
                                            <ToggleInput
                                                checked={
                                                    agentSteps[currentStepIndex]
                                                        ?.approvalPossible as boolean
                                                }
                                                name={"approvalPossible"}
                                                onChange={(e) => {
                                                    handleAgentStepChange(
                                                        currentStepIndex,
                                                        "approvalPossible",
                                                        e.target.checked ? true : false
                                                    );
                                                }}
                                                className="w-slider before:!h-4 before:!w-4 h-5 w-[30px] before:left-[2px] rounded-[30px]"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                    <p className="text-slate-500 text-sm">
                                        {agentSteps[currentStepIndex]?.approvalPossible
                                            ? "Select approver"
                                            : "Add custom instructions"}
                                    </p>
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                </div>

                                {agentSteps[currentStepIndex]?.approvalPossible && (
                                    <SelectInput
                                        value={
                                            agentSteps[currentStepIndex]?.approverName ||
                                            (agentSteps[currentStepIndex]
                                                ?.approvalIdString as string) ||
                                            ""
                                        }
                                        name="approvalIdString"
                                        nameFirst={true}
                                        placeholder={"Select approver"}
                                        handleChange={(name, value) => {
                                            handleApproverChange(value);
                                        }}
                                        handleInputChange={debounce((evt) => {
                                            setDebouncedSearchTeamMember(evt.target.value);
                                        }, 800)}
                                        isRequired={agentSteps[currentStepIndex]?.approvalPossible}
                                        searchLoading={fetchingTeamMember}
                                        dropdownOptions={teamMembers?.map((member) => ({
                                            label: member?.fullName
                                                ? member?.fullName
                                                : member?.email,
                                            value: member?._id,
                                        }))}
                                        dropdownClassName="!pb-0 !max-h-[320px]"
                                        rightDropdownContainerClassName="!sticky bottom-0"
                                        customRightDropdownContainer={
                                            <div className="py-1 px-1  flex items-center  bg-white !h-[48px] sticky w-full bottom-0 border-t border-slate-200">
                                                <div
                                                    className="flex items-center w-fit cursor-pointer gap-2"
                                                    onClick={() => {
                                                        setAddTeamMemberModal(true);
                                                    }}
                                                >
                                                    <i className="ri-add-fill text-[#D39901] text-[10px] rounded-full py-0.5 px-1 bg-[#FFF5DA]"></i>

                                                    <span className="text-[12px] font-rocGroteskMedium text-[#D39901] underline">
                                                        Add a new team-mate
                                                    </span>
                                                </div>
                                            </div>
                                        }
                                    />
                                )}

                                <TextareaInput
                                    name={"customInstructions"}
                                    value={
                                        (agentSteps[currentStepIndex]
                                            ?.customInstructions as string) || ""
                                    }
                                    onChange={(e) => {
                                        handleAgentStepChange(
                                            currentStepIndex,
                                            "customInstructions",
                                            e.target.value
                                        );
                                    }}
                                    rows={3}
                                    placeholder={"Enter custom instructions"}
                                    inputPlaceholder="Provide any specific instructions you'd like the AI Agent to follow. "
                                    required={false}
                                />
                            </div>

                            <div className="mt-4">
                                <p className="text-sm text-slate-500 p-3 bg-slate-100 border border-slate-200 rounded">
                                    Custom instructions let you tailor how your AI Agent behaves in
                                    specific steps of a workflow. Use them to guide the Agent's
                                    tone, priorities, or handling of unique business rules. This
                                    helps ensure your AI Agent aligns with your brand's voice and
                                    operational standards.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </AgentSideDrawer>

            {/* Update Inventory, update_inventory */}
            <AgentSideDrawer open={currentStep === "update_inventory"} modalRef={modalRef}>
                <div className=" flex  !bg-white flex-col  font-rocGroteskMedium  ">
                    <div className="relative">
                        <div className="px-6 py-6 !sticky !top-0 bg-white !z-10 ">
                            <div className="flex justify-between  items-center">
                                <p className="text-sm font-rocGroteskMedium text-slate-800">
                                    AI Teammate's customization
                                </p>
                                <i
                                    className="ri-close-fill text-xl text-slate-500 cursor-pointer border border-slate-200 rounded py-0 px-1"
                                    onClick={() => {
                                        setCurrentStep("");
                                    }}
                                ></i>
                            </div>
                            <p className="text-sm text-slate-500 ">
                                Set how you want this AI team-mate to work
                            </p>
                        </div>
                        <div className="mx-6 border border-slate-200 p-4 rounded-lg">
                            <div className="space-y-3">
                                <div className="flex justify-between">
                                    <div className="flex gap-0.5 items-center">
                                        <p className="text-sm text-slate-800">Update Inventory</p>
                                        <SynthTooltip
                                            divId={agentSteps[currentStepIndex]?.key}
                                            position={"top"}
                                            icon={
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            }
                                            contentIcon={<></>}
                                            title=""
                                            content={agentSteps[currentStepIndex]?.description}
                                            styling={{
                                                width: "270px",
                                                background: "#142837",
                                                borderRadius: "6px",
                                            }}
                                            contentClass="text-xs"
                                        />
                                    </div>

                                    {/* {agentSteps[currentStepIndex]?.showApproval && ( */}
                                    <div className="flex items-center gap-1">
                                        <p className="text-gm-45 text-xs">
                                            <p className="text-gm-45 text-xs">
                                                Turn{" "}
                                                {agentSteps[currentStepIndex]?.approvalPossible
                                                    ? "off"
                                                    : "on"}{" "}
                                                need for approval
                                            </p>
                                        </p>
                                        <ToggleInput
                                            checked={
                                                agentSteps[currentStepIndex]
                                                    ?.approvalPossible as boolean
                                            }
                                            name={"approvalPossible"}
                                            onChange={(e) => {
                                                handleAgentStepChange(
                                                    currentStepIndex,
                                                    "approvalPossible",
                                                    e.target.checked ? true : false
                                                );
                                            }}
                                            className="w-slider before:!h-4 before:!w-4 h-5 w-[30px] before:left-[2px] rounded-[30px]"
                                        />
                                    </div>
                                    {/* )} */}
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                    <p className="text-slate-500 text-sm">Select approver</p>
                                    <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                </div>

                                {agentSteps[currentStepIndex]?.approvalPossible && (
                                    <SelectInput
                                        value={
                                            agentSteps[currentStepIndex]?.approverName ||
                                            (agentSteps[currentStepIndex]
                                                ?.approvalIdString as string) ||
                                            ""
                                        }
                                        name="approvalIdString"
                                        nameFirst={true}
                                        placeholder={"Select approver"}
                                        handleChange={(name, value) => {
                                            handleApproverChange(value);
                                        }}
                                        handleInputChange={debounce((evt) => {
                                            setDebouncedSearchTeamMember(evt.target.value);
                                        }, 800)}
                                        isRequired={agentSteps[currentStepIndex]?.approvalPossible}
                                        searchLoading={fetchingTeamMember}
                                        dropdownOptions={teamMembers?.map((member) => ({
                                            label: member?.fullName
                                                ? member?.fullName
                                                : member?.email,
                                            value: member?._id,
                                        }))}
                                        dropdownClassName="!pb-0 !max-h-[320px]"
                                        rightDropdownContainerClassName="!sticky bottom-0"
                                        customRightDropdownContainer={
                                            <div className="py-1 px-1  flex items-center  bg-white !h-[48px] sticky w-full bottom-0 border-t border-slate-200">
                                                <div
                                                    className="flex items-center w-fit cursor-pointer gap-2"
                                                    onClick={() => {
                                                        setAddTeamMemberModal(true);
                                                    }}
                                                >
                                                    <i className="ri-add-fill text-[#D39901] text-[10px] rounded-full py-0.5 px-1 bg-[#FFF5DA]"></i>

                                                    <span className="text-[12px] font-rocGroteskMedium text-[#D39901] underline">
                                                        Add a new team-mate
                                                    </span>
                                                </div>
                                            </div>
                                        }
                                    />
                                )}

                                <TextareaInput
                                    name={"customInstructions"}
                                    value={
                                        (agentSteps[currentStepIndex]
                                            ?.customInstructions as string) || ""
                                    }
                                    onChange={(e) => {
                                        handleAgentStepChange(
                                            currentStepIndex,
                                            "customInstructions",
                                            e.target.value
                                        );
                                    }}
                                    rows={3}
                                    placeholder={"Enter custom instructions"}
                                    inputPlaceholder="Provide any specific instructions you'd like the AI Agent to follow. "
                                    required={false}
                                />
                            </div>

                            <div className="mt-4">
                                <p className="text-sm text-slate-500 p-3 bg-slate-100 border border-slate-200 rounded">
                                    Custom instructions let you tailor how your AI Agent behaves in
                                    specific steps of a workflow. Use them to guide the Agent's
                                    tone, priorities, or handling of unique business rules. This
                                    helps ensure your AI Agent aligns with your brand's voice and
                                    operational standards.
                                </p>
                            </div>
                        </div>
                        {agentSteps[currentStepIndex]?.actions?.length > 0 && (
                            <div className="mb-10">
                                <div className="flex justify-between my-6 mx-6">
                                    <div className="flex gap-1 items-center">
                                        <p className="text-sm text-[#0046A2]">Next step</p>
                                        <span className="text-xs text-slate-500">(Optional)</span>
                                        <SynthTooltip
                                            divId={"optional-next-steps"}
                                            position={"top"}
                                            icon={
                                                <i className="ri-information-fill text-slate-300 text-sm"></i>
                                            }
                                            contentIcon={<></>}
                                            title=""
                                            content={
                                                agentSteps[currentStepIndex]?.actions?.[0]
                                                    ?.description
                                            }
                                            styling={{
                                                width: "220px",
                                                background: "#142837",
                                                borderRadius: "6px",
                                            }}
                                            contentClass="text-xs"
                                        />
                                    </div>
                                </div>

                                <div className="mx-6 border border-slate-200 p-4 rounded-lg mb-8">
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                            <p className="text-slate-500 text-sm">Action</p>
                                            <p className="bg-slate-200 flex-1 h-[1px] w-full"></p>
                                        </div>

                                        <SelectInput
                                            value={
                                                selectedAction ||
                                                (agentSteps[currentStepIndex]
                                                    ?.selectedAction as string)
                                                    ? selectedAction ||
                                                      (agentSteps[currentStepIndex]
                                                          ?.selectedAction as string)
                                                    : actionChanged
                                                    ? "No Action"
                                                    : ""
                                            }
                                            name="selectedAction"
                                            placeholder={"Select control action"}
                                            handleChange={(name, value) => {
                                                if (value === "no_action") {
                                                    setSelectedAction("");
                                                    setActionChanged(true);
                                                } else {
                                                    setSelectedAction(value);
                                                    setActionChanged(false);
                                                }
                                            }}
                                            // isRequired={true}
                                            dropdownOptions={[
                                                {
                                                    label: "No Action",
                                                    value: "no_action",
                                                },
                                                ...agentSteps[currentStepIndex]?.actions?.map(
                                                    (action) => ({
                                                        label:
                                                            camelCaseToRegularCase(action?.name) ===
                                                            "Notify User"
                                                                ? "Notify me"
                                                                : camelCaseToRegularCase(
                                                                      action?.name
                                                                  ),
                                                        value: action?.name,
                                                    })
                                                ),
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AgentSideDrawer>
        </>
    );
};

export default InventoryManagement;
